import { makeStyles } from '@mui/styles';
import { IconButton } from 'apollo-react';
import Tab from 'apollo-react/components/Tab';
import Tabs from 'apollo-react/components/Tabs';
import TreeItem from 'apollo-react/components/TreeItem';
import TreeView from 'apollo-react/components/TreeView';
import { ChevronLeft, ChevronRight } from 'apollo-react-icons';
import _ from 'lodash';
import { Resizable } from 're-resizable';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from '../../../custom-hooks/useTranslation';
import { equalityFn } from '../../../form-designer/utils/form-manager.utils';
import {
  addNewView,
  setDesignPanelWidth,
  setIsDesignPanelOpened,
} from '../../actions/report-model-designer.actions';
import {
  DATA_MODEL_TAB,
  MAX_DESIGN_PANEL_WIDTH,
  MIN_DESIGN_PANEL_WIDTH,
} from '../../constants/report-model-designer.constants';
import {
  getDataModel,
  getDesignPanelWidth,
  getIsDesignPanelOpened,
} from '../../selectors/report-model-designer.selectors';

const useStyles = makeStyles({
  root: {
    borderLeft: '1px solid #e9e9e9',
    width: '100%',
    padding: 15,
    height: 'calc(100vh - 147px)',
    overflowY: 'auto',
    '& svg': {
      width: 16,
    },
  },
  tabContent: {
    backgroundColor: '#fff',
    paddingTop: 10,
  },
  handleContainer: {
    left: '-10px!important',
    '&:hover': {
      borderRight: '3px solid #0768fd',
    },
    '&:active': {
      borderRight: '3px solid #0768fd',
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 0,
    marginRight: 14,
    marginBottom: 18,
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    color: '#000',
    marginRight: 'auto',
  },
  icon_button: {
    position: 'absolute',
    top: '40px',
    left: '-10px',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
    border: 'solid 1px #d9d9d9',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99,
    '& svg': {
      fontSize: '16px',
      zIndex: 99,
    },
  },
  icon_button_properties: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
    border: 'solid 1px #d9d9d9',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9,
    '& svg': {
      fontSize: '16px',
      zIndex: 99,
    },
  },
  icon_button_properties_container: {
    width: '0px !important',
    position: 'absolute',
    top: '5px',
    right: '5px',
    display: 'flex',
    alignItems: 'center',
  },
  infoContent: {
    marginTop: 14,
    fontFamily: 'Proxima Nova',
    fontSize: 14,
    fontStyle: 'normal',
    fontStretch: 'normal',
    letterSpacing: 'normal',
    color: 'var(--black)',
  },
});

const renderTree = ({ Id, Label, InnerTreeBranches, InnerTreeLeaves, SsdEntity }) => {
  const childNodes = InnerTreeLeaves?.length > 0 ? InnerTreeLeaves : InnerTreeBranches || [];

  const label = _.isEmpty(childNodes) ? `${Label} (${SsdEntity})` : Label;

  return (
    <TreeItem key={Id} nodeId={Id} label={label} style={{ marginLeft: 0 }}>
      {Array.isArray(childNodes) ? childNodes.map((node) => renderTree(node)) : null}
    </TreeItem>
  );
};

const DesignPanel = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const isDesignPanelOpened = useSelector(getIsDesignPanelOpened, equalityFn);
  const dataModel = useSelector(getDataModel, equalityFn);
  const designPanelWidth = useSelector(getDesignPanelWidth, equalityFn);

  const [click, setClick] = useState(0);

  const doubleClick = (nodeId) => {
    dispatch(addNewView(nodeId));
  };

  const handleSelect = (e, nodeId) => {
    e.stopPropagation();
    setClick((prev) => prev + 1);

    setTimeout(() => setClick(0), 200);

    click > 0 && doubleClick(nodeId);
  };

  const onClose = () => {
    dispatch(setIsDesignPanelOpened(false));
  };

  const onOpen = () => {
    dispatch(setIsDesignPanelOpened(true));
  };

  const onResize = (e, direction, ref, d) => {
    dispatch(setDesignPanelWidth(designPanelWidth + d.width));
  };

  return (
    <>
      {isDesignPanelOpened ? (
        <Resizable
          minWidth={MIN_DESIGN_PANEL_WIDTH}
          maxWidth={MAX_DESIGN_PANEL_WIDTH}
          size={{ width: designPanelWidth }}
          onResizeStop={onResize}
          enable={{ left: true }}
          handleClasses={{ left: classes.handleContainer }}
        >
          <div className={classes.root}>
            <div className={classes.header}>
              <div className={classes.title}>{translateString('Design Panel')}</div>
              <div className={classes.icon_button}>
                <IconButton onClick={onClose} aria-label={translateString('Design Panel')}>
                  <ChevronRight />
                </IconButton>
              </div>
            </div>

            <div className={classes.infoContent}>
              {translateString(
                'Select a row from any table of your choice in the canvas and double click on the Entity name available under the Data Model to add a row to the selected table in the canvas.'
              )}
            </div>

            <Tabs value={DATA_MODEL_TAB} size="small">
              <Tab label={DATA_MODEL_TAB} value={DATA_MODEL_TAB} />
            </Tabs>
            <div className={classes.tabContent}>
              {dataModel?.TreeBranch && (
                <TreeView variant="folder" onNodeSelect={handleSelect}>
                  {[dataModel.TreeBranch].map((node) => renderTree(node))}
                </TreeView>
              )}
            </div>
          </div>
        </Resizable>
      ) : (
        <div className={classes.icon_button_properties_container}>
          <IconButton
            className={classes.icon_button_properties}
            onClick={onOpen}
            aria-label={translateString('Design Panel')}
          >
            <ChevronLeft />
          </IconButton>
        </div>
      )}
    </>
  );
};

export default DesignPanel;
